import { InputAdornment, TextField } from '@mui/material';
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  ToolbarButton,
  useGridRootProps,
} from '@mui/x-data-grid';
import {
  GridToolbarDivider,
  type GridToolbarProps,
} from '@mui/x-data-grid/internals';
import COLORS from '@/resources/style/Themes';

const QUICK_FILTER_WIDTH = 400;
export const SEARCH_FIELD_BORDER_RADIUS = 3;
const DEFAULT_QUICK_FILTER_DEBOUNCE_MS = 500;
const SEARCH_PLACEHOLDER_FONT_SIZE = 13;

/**
 * Data Grid toolbar that keeps the quick-filter input expanded.
 * The default GridToolbar quick filter is a collapsed search icon and does not
 * forward `expanded` through `quickFilterProps`.
 */
export const PersistentQuickFilterToolbar = ({
  showQuickFilter = true,
  quickFilterProps,
  csvOptions: _csvOptions,
  printOptions: _printOptions,
  mainControls,
  additionalExportMenuItems: _additionalExportMenuItems,
  ...other
}: GridToolbarProps) => {
  const rootProps = useGridRootProps();
  const { localeText } = rootProps;
  const BaseTooltip = rootProps.slots.baseTooltip;
  const ColumnSelectorIcon = rootProps.slots.columnSelectorIcon;
  const BaseBadge = rootProps.slots.baseBadge;
  const OpenFilterButtonIcon = rootProps.slots.openFilterButtonIcon;
  const QuickFilterIcon = rootProps.slots.quickFilterIcon;
  const QuickFilterClearIcon = rootProps.slots.quickFilterClearIcon;
  const placeholder =
    quickFilterProps?.slotProps?.root?.placeholder ??
    localeText.toolbarQuickFilterPlaceholder;

  return (
    <Toolbar {...other}>
      {mainControls ?? (
        <>
          {!rootProps.disableColumnSelector && (
            <BaseTooltip title={localeText.toolbarColumns}>
              <ColumnsPanelTrigger render={<ToolbarButton />}>
                <ColumnSelectorIcon fontSize="small" />
              </ColumnsPanelTrigger>
            </BaseTooltip>
          )}
          {!rootProps.disableColumnFilter && (
            <BaseTooltip title={localeText.toolbarFilters}>
              <FilterPanelTrigger
                render={(triggerProps, state) => (
                  <ToolbarButton
                    {...triggerProps}
                    color={state.filterCount > 0 ? 'primary' : 'default'}
                  >
                    <BaseBadge
                      badgeContent={state.filterCount}
                      color="primary"
                      variant="dot"
                    >
                      <OpenFilterButtonIcon fontSize="small" />
                    </BaseBadge>
                  </ToolbarButton>
                )}
              />
            </BaseTooltip>
          )}
        </>
      )}
      {showQuickFilter && (
        <>
          <GridToolbarDivider />
          <QuickFilter
            expanded
            debounceMs={
              quickFilterProps?.debounceMs ?? DEFAULT_QUICK_FILTER_DEBOUNCE_MS
            }
            parser={quickFilterProps?.quickFilterParser}
            formatter={quickFilterProps?.quickFilterFormatter}
          >
            <QuickFilterControl
              render={({ ref, ...controlProps }) => (
                <TextField
                  {...controlProps}
                  sx={{
                    p:1,
                    width: QUICK_FILTER_WIDTH,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: SEARCH_FIELD_BORDER_RADIUS,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.GREY,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: SEARCH_PLACEHOLDER_FONT_SIZE,
                      fontStyle: 'italic',
                      color: COLORS.GREY,
                      opacity: 1,
                    },
                  }}
                  inputRef={ref}
                  aria-label={placeholder}
                  placeholder={placeholder}
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <QuickFilterIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: controlProps.value ? (
                        <InputAdornment position="end">
                          <QuickFilterClear
                            edge="end"
                            size="small"
                            aria-label={localeText.toolbarQuickFilterDeleteIconLabel}
                          >
                            <QuickFilterClearIcon fontSize="small" />
                          </QuickFilterClear>
                        </InputAdornment>
                      ) : null,
                      ...controlProps.slotProps?.input,
                    },
                    ...controlProps.slotProps,
                  }}
                />
              )}
            />
          </QuickFilter>
        </>
      )}
    </Toolbar>
  );
};
